import {
  CITY_STATS,
  DISTRICT_STATS,
  FLAGGED_REQUESTS,
  WATCH_DISTRICTS,
  type DashboardStats,
} from '../data/requestDashboard'

export type Lang = 'fr' | 'en'

/** Where a dashboard link leads: map filtered by state, open requests, or the service table. */
export type AttentionTarget = 'map-late' | 'map-risk' | 'map-open' | 'sla-table'

export const fmtNum = (n: number, lang: Lang) =>
  Math.round(n).toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')

export const fmtDec = (n: number, lang: Lang) =>
  n.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

/** Signed percent change, e.g. "+11 %" / "−5 %". */
export function pctChange(current: number, previous: number, lang: Lang) {
  const v = Math.round(((current - previous) / previous) * 100)
  const sign = v > 0 ? '+' : v < 0 ? '−' : '±'
  return lang === 'fr' ? `${sign}${Math.abs(v)} %` : `${sign}${Math.abs(v)}%`
}

/** Late / at-risk requests in the scope (whole city when null). */
export function flaggedFor(districtId: string | null) {
  return districtId === null
    ? FLAGGED_REQUESTS
    : FLAGGED_REQUESTS.filter((r) => r.districtIds.includes(districtId))
}

/** Whether any late / at-risk request falls in the scope. */
export function scopeHasFlagged(districtId: string | null) {
  return flaggedFor(districtId).length > 0
}

/**
 * Figures for the scope and period. Period counts scale with the period
 * length (base = 30 days); rates and open requests don't.
 */
export function statsFor(districtId: string | null, periodDays: number): DashboardStats {
  let base: DashboardStats = CITY_STATS
  if (districtId) {
    const known = DISTRICT_STATS[districtId]
    if (known) base = known
    else {
      // District without dedicated demo figures: 1/12 of the city.
      const f = 1 / 12
      const flagged = flaggedFor(districtId)
      const late = flagged.filter((r) => r.state === 'late').length
      const atRisk = flagged.length - late
      const watch = WATCH_DISTRICTS[districtId]
      base = {
        ...CITY_STATS,
        ...(watch ? { onTime: watch.onTime, onTimePrev: watch.onTimePrev } : {}),
        received: Math.round(CITY_STATS.received * f),
        receivedPrev: Math.round(CITY_STATS.receivedPrev * f),
        surveyResponses: Math.round(CITY_STATS.surveyResponses * f),
        issues: CITY_STATS.issues.map((i) => ({
          ...i,
          count: Math.round(i.count * f),
          previous: Math.round(i.previous * f),
        })),
        open: Math.round(CITY_STATS.open * f) + flagged.length,
        openAtRisk: atRisk,
        late,
        latePrev: watch ? watch.latePrev : late,
      }
    }
  }
  const p = periodDays / 30
  if (p === 1) return base
  const scale = (n: number) => Math.round(n * p)
  return {
    ...base,
    received: scale(base.received),
    receivedPrev: scale(base.receivedPrev),
    surveyResponses: scale(base.surveyResponses),
    issues: base.issues.map((i) => ({ ...i, count: scale(i.count), previous: scale(i.previous) })),
  }
}
